import React, { useCallback, useEffect, useMemo, useState } from "react";
import BurndownChart from "@/components/charts/BurndownChart";
import { BusinessPlanDocumentView } from "@/components/business-plan/BusinessPlanDocumentView";
import { TimelineZoom, type ZoomLevel } from "@/components/atoms";
import { Button } from "@/components/atoms";
import { AppLogo } from "@/components/atoms/AppLogo";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { InvestorCommentsSection } from "@/components/investor/InvestorCommentsSection";
import {
  MobileBurndownChart,
  MobileCharts,
  MobileFinancialSummary,
  MobileTimelineControls,
} from "@/components/mobile";
import {
  buildDefaultCustomRange,
  panCustomRange,
  panDateByZoom,
  DEFAULT_ZOOM_LEVEL,
  calculateVisibleDateRange,
  type TimelineCustomRange,
} from "@/utils/chart/timeline-range.utils";
import { getDefaultTimelineDate, clampDateToRange } from "@/utils/chart/timeline-date.utils";
import { calculateBurndownSnapshot } from "@/utils/chart/burndown.utils";
import { mapInvestorPayloadToChartInput } from "@/utils/investor-data.utils";
import type { InvestorSession, investorAPI } from "@/services/api/investor.service";
import type {
  Expense,
  FinancialEvent,
  Revenue,
  TeamMember,
  CustomerProjection,
  SubscriptionTier,
  CustomerCapacityGroup,
  BusinessPlanDocument,
} from "@/types/startup.types";
import type { BurndownChartProps } from "@/types/burndown.types";

type InvestorFinancialDashboardProps = {
  data: Record<string, unknown>;
  session: InvestorSession;
};

export const InvestorFinancialDashboard: React.FC<InvestorFinancialDashboardProps> = ({
  data,
  session,
}) => {
  const mapped = useMemo(() => mapInvestorPayloadToChartInput(data), [data]);
  const chartData = mapped.chartData as unknown as BurndownChartProps["data"];
  const [activeView, setActiveView] = useState<"dashboard" | "plan">("dashboard");
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(DEFAULT_ZOOM_LEVEL);
  const [zoomCenter, setZoomCenter] = useState<Date | null>(null);
  const [scrubDate, setScrubDate] = useState<Date | null>(null);
  const [customDateRange, setCustomDateRange] = useState<TimelineCustomRange | null>(
    null,
  );
  const [comments, setComments] = useState<
    Awaited<ReturnType<typeof investorAPI.getComments>>
  >([]);
  const [commentSection, setCommentSection] = useState("Financial Snapshot");
  const [commentText, setCommentText] = useState("");
  const [commentError, setCommentError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { investorAPI: api } = await import("@/services/api/investor.service");
      try {
        const result = await api.getComments(session);
        if (!cancelled) {
          setComments(result);
        }
      } catch {
        // non-blocking
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const handleZoomChange = useCallback(
    (level: ZoomLevel) => {
      setZoomLevel(level);
      if (level === "custom") {
        setCustomDateRange(
          (prev) => prev ?? buildDefaultCustomRange(chartData.startDate),
        );
      }
      setZoomCenter(null);
    },
    [chartData.startDate],
  );

  const handlePanLeft = useCallback(() => {
    if (zoomLevel === "custom" && customDateRange) {
      setCustomDateRange(panCustomRange(customDateRange, "left"));
      return;
    }
    const currentDate = zoomCenter || getDefaultTimelineDate(chartData.startDate, chartData.endDate);
    setZoomCenter(panDateByZoom(currentDate, zoomLevel, "left"));
  }, [zoomLevel, zoomCenter, customDateRange, chartData.startDate, chartData.endDate]);

  const handlePanRight = useCallback(() => {
    if (zoomLevel === "custom" && customDateRange) {
      setCustomDateRange(panCustomRange(customDateRange, "right"));
      return;
    }
    const currentDate = zoomCenter || getDefaultTimelineDate(chartData.startDate, chartData.endDate);
    setZoomCenter(panDateByZoom(currentDate, zoomLevel, "right"));
  }, [zoomLevel, zoomCenter, customDateRange, chartData.startDate, chartData.endDate]);

  const visibleDateRange = useMemo(
    () =>
      calculateVisibleDateRange({
        zoomLevel,
        zoomCenter,
        startupStartDate: chartData.startDate,
        startupEndDate: chartData.endDate,
        customRange: customDateRange,
      }),
    [
      zoomLevel,
      zoomCenter,
      customDateRange,
      chartData.startDate,
      chartData.endDate,
    ],
  );

  const timelineDate = useMemo(
    () =>
      scrubDate ??
      getDefaultTimelineDate(chartData.startDate, chartData.endDate),
    [scrubDate, chartData.startDate, chartData.endDate],
  );

  const handleMobileDateChange = useCallback(
    (date: Date) => {
      setScrubDate(clampDateToRange(date, visibleDateRange));
    },
    [visibleDateRange],
  );

  useEffect(() => {
    if (!scrubDate) {
      return;
    }
    const clamped = clampDateToRange(scrubDate, visibleDateRange);
    if (clamped.getTime() !== scrubDate.getTime()) {
      setScrubDate(clamped);
    }
  }, [scrubDate, visibleDateRange]);

  const currentSnapshot = useMemo(
    () => calculateBurndownSnapshot(chartData, timelineDate),
    [chartData, timelineDate],
  );

  const submitComment = async () => {
    setCommentError(null);
    try {
      const { investorAPI: api } = await import("@/services/api/investor.service");
      const created = await api.postComment(session, commentSection, commentText.trim());
      setComments((prev) => [created, ...prev]);
      setCommentText("");
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : "Failed to post comment");
    }
  };

  const rawExpenses = (data.expenses as Expense[]) ?? [];
  const rawRevenues = (data.revenues as Revenue[]) ?? [];
  const rawEvents = (data.events as FinancialEvent[]) ?? [];
  const customerProjections = (data.customerProjections as CustomerProjection[]) ?? [];
  const subscriptionTiers = (data.subscriptionTiers as SubscriptionTier[]) ?? [];
  const capacityGroups = (data.capacityGroups as CustomerCapacityGroup[]) ?? [];

  const commentsSection = (
    <InvestorCommentsSection
      comments={comments}
      commentSection={commentSection}
      commentText={commentText}
      commentError={commentError}
      onSectionChange={setCommentSection}
      onTextChange={setCommentText}
      onSubmit={submitComment}
    />
  );

  return (
    <div className="flex min-h-screen min-w-0 flex-col overflow-x-hidden bg-page text-white">
      <header className="border-b border-white/10 bg-surface/60 px-4 py-4 sm:px-6 sm:py-5 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3 sm:gap-4">
            <AppLogo size={32} showWordmark={false} className="mt-0.5 hidden sm:flex" />
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand">
                Investor view
              </p>
              <h1 className="text-xl font-black sm:text-2xl">{mapped.startupMeta.name}</h1>
              {mapped.startupMeta.tagline ? (
                <p className="mt-1 text-sm text-white/60">{mapped.startupMeta.tagline}</p>
              ) : null}
              <p className="mt-1 text-xs text-white/45">
                {session.linkLabel} · Viewing as {session.displayName}
              </p>
            </div>
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            {mapped.businessPlan ? (
              <Button
                type="button"
                variant={activeView === "plan" ? "primary" : "secondary"}
                size="sm"
                className="flex-1 sm:flex-none"
                onClick={() => setActiveView("plan")}
              >
                Business plan
              </Button>
            ) : null}
            <Button
              type="button"
              variant={activeView === "dashboard" ? "primary" : "secondary"}
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => setActiveView("dashboard")}
            >
              Financials
            </Button>
          </div>
        </div>
      </header>

      <main className="w-full min-w-0 flex-1 overflow-x-hidden px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
        {activeView === "plan" && mapped.businessPlan ? (
          <div className="min-w-0 overflow-x-hidden rounded-[20px] border border-white/[0.09] bg-surface">
            <BusinessPlanDocumentView plan={mapped.businessPlan as BusinessPlanDocument} />
          </div>
        ) : (
          <>
            {/* Mobile: summary, zoom, charts — no desktop-only burndown scrubber */}
            <div className="space-y-4 lg:hidden">
              <div className="-mx-4 sm:-mx-6">
                <MobileFinancialSummary
                  currentSnapshot={currentSnapshot}
                  customerProjectionData={chartData.customerProjectionData}
                  currentDate={timelineDate}
                />
                <MobileTimelineControls
                  zoomLevel={zoomLevel}
                  onZoomChange={handleZoomChange}
                  customRange={customDateRange}
                  onCustomRangeChange={setCustomDateRange}
                  onPanLeft={handlePanLeft}
                  onPanRight={handlePanRight}
                  onGoToToday={() => {
                    setZoomCenter(null);
                    setScrubDate(null);
                  }}
                  onGoToStart={() => setScrubDate(new Date(chartData.startDate))}
                  onGoToEnd={() => setScrubDate(new Date(chartData.endDate))}
                />
                <MobileBurndownChart
                  data={chartData}
                  currentDate={timelineDate}
                  visibleDateRange={visibleDateRange}
                  rawEvents={rawEvents}
                  onDateChange={handleMobileDateChange}
                />
                <MobileCharts
                  data={chartData}
                  visibleDateRange={visibleDateRange}
                  events={rawEvents}
                  currentSnapshot={currentSnapshot}
                />
              </div>
              {commentsSection}
            </div>

            {/* Desktop: full burndown chart */}
            <div className="hidden space-y-6 lg:block">
              <div className="flex justify-end">
                <TimelineZoom
                  zoomLevel={zoomLevel}
                  onZoomChange={handleZoomChange}
                  customRange={customDateRange}
                  onCustomRangeChange={setCustomDateRange}
                  onPanLeft={handlePanLeft}
                  onPanRight={handlePanRight}
                  onGoToToday={() => setZoomCenter(null)}
                  onGoToStart={() => setZoomCenter(new Date(chartData.startDate))}
                  onGoToEnd={() => setZoomCenter(new Date(chartData.endDate))}
                />
              </div>

              <div className="overflow-hidden rounded-[20px] border border-white/[0.09] bg-surface">
                <BurndownChart
                  data={chartData as never}
                  zoomLevel={zoomLevel}
                  zoomCenter={zoomCenter}
                  customDateRange={customDateRange}
                  rawExpenses={rawExpenses}
                  rawRevenues={rawRevenues}
                  rawEvents={rawEvents}
                  startupId={session.startupId}
                  customers={customerProjections}
                  subscriptionTiers={subscriptionTiers}
                  capacityGroups={capacityGroups}
                  teamMembers={(mapped.teamMembers as unknown as TeamMember[]) ?? []}
                  customerGrowthModels={(data.customerGrowthScenarios as never[]) ?? []}
                  customerAcquisitionCosts={(data.acquisitionCostModels as never[]) ?? []}
                  readOnly
                />
              </div>

              {commentsSection}
            </div>
          </>
        )}
      </main>

      <SiteFooter
        variant="compact"
        showBrandMark
        extraNote="Made with burn-chart.com by Soki Creative LLC"
      />
    </div>
  );
};
